import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Link,
  CircularProgress,
  IconButton,
} from "@mui/material";
import Card from "@/components/Card";
import ConfirmProjectSignatureModal from "@/components/ConfirmProjectSignatureModal";
import EnrollmentStatusStepper from "./EnrollmentStatusStepper";
import { useProjectsStore } from "@/store/projectsStore";
import { updateProject } from "@/api/projectsApi";
import { updateContractorProjectReq } from "@/api/contractorApi";
import { useAuthStore } from "@/store/authStore.ts";
import { formatShortMonthDate } from "@/utils/formatDate";
import { getActionLinks } from "@/utils/getActionLinks";
import { useProjectDocument } from "@/hooks/useProjectDocument";
import SvgEyeShow from "@/assets/icons/SVGEyeShow";
import {
  getErrorTitle,
  getErrorDetails,
  handleRetry,
  handleContactSupport,
} from "@/utils/errorHandlers";

const ErrorMessageBox: React.FC<{
  errorStep?: number;
  errorMessage?: string;
}> = ({ errorStep, errorMessage }) => {
  if (!errorStep || !errorMessage) return null;

  const handleRetryClick = () => {
    handleRetry(errorStep);
  };

  const handleContactSupportClick = () => {
    handleContactSupport();
  };
  return (
    <Box
      sx={{
        border: "1px solid #FFB366",
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        padding: "16px",
        marginTop: "16px",
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
      }}
    >
      <Box
        sx={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: "#FF4444",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          marginTop: "2px",
        }}
      >
        <Typography
          sx={{
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          !
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#1A1C1E",
              fontFamily: "Libre Franklin, Arial, sans-serif",
              lineHeight: "1.4em",
              marginBottom: "8px",
            }}
          >
            {getErrorTitle(errorStep)}
          </Typography>

          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              color: "#666666",
              fontFamily: "Inter, sans-serif",
              lineHeight: "1.5em",
            }}
          >
            {getErrorDetails(errorStep)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "16px",
            marginLeft: "16px",
            flexShrink: 0,
          }}
        >
          <Link
            component="button"
            onClick={handleRetryClick}
            sx={{
              color: "#0076CE",
              fontSize: "14px",
              fontWeight: 400,
              textDecoration: "none",
              fontFamily: "Inter, sans-serif",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {getActionLinks(errorStep).primary}
          </Link>
          {getActionLinks(errorStep).secondary && (
            <Link
              component="button"
              onClick={handleContactSupportClick}
              sx={{
                color: "#1A1C1E",
                fontSize: "14px",
                fontWeight: 400,
                textDecoration: "none",
                fontFamily: "Inter, sans-serif",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {getActionLinks(errorStep).secondary}
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const EnrollmentProcessingCard: React.FC = () => {
  const role = useAuthStore((state) => state.role);
  const isContractor = role === "contractor";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enrollmentStep, setEnrollmentStep] = useState(1);
  const [lastActionDate, setLastActionDate] = useState<string | null>(null);
  const [errorStep, setErrorStep] = useState<number | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { projectDetails, setProjectsDetails } = useProjectsStore();
  const { handleDownloadDocument, handlePreviewDocument, isDownloading } =
    useProjectDocument();

  const handleDownloadClick = async () => {
    try {
      await handleDownloadDocument(projectDetails);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Download failed"
      );
      setErrorStep(1);
    }
  };

  const isProjectLoaded =
    projectDetails && Object.keys(projectDetails).length > 0;

  const hasDocumentBeenSent = projectDetails?.document_url;

  const hasSizingToolBeenPassed = Boolean(projectDetails?.sizing_passed);

  const shouldShowProgress = hasDocumentBeenSent || lastActionDate;

  if (!hasSizingToolBeenPassed) {
    return null;
  }

  const getCurrentStep = () => {
    if (projectDetails?.complete) {
      return 4;
    } else if (projectDetails?.document_url) {
      return 3;
    } else if (lastActionDate) {
      return enrollmentStep;
    }
    return 1;
  };

  const currentStep = getCurrentStep();

  const handleSignDocument = () => {
    setIsModalOpen(true);
  };

  const handleSubmitPDF = async () => {
    if (!projectDetails?.id) {
      console.error("No project ID available");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("enrolmentStatus", "active");

      if (isContractor) {
        await updateContractorProjectReq(projectDetails.id, formData);
      } else {
        await updateProject(projectDetails.id, formData);
      }
      setProjectsDetails({ ...projectDetails, enrolmentStatus: "active" });

      setEnrollmentStep(2);
      setLastActionDate(new Date().toLocaleDateString());
      setIsModalOpen(false);
      setErrorStep(undefined);
      setErrorMessage("");
    } catch (error) {
      console.error("Error updating project status:", error);
      setErrorStep(4);
      setErrorMessage(
        "Document sent successfully, but there was an issue updating the project status. Please contact support if this persists."
      );
    }
  };

  // Show loading spinner while project details are being loaded
  if (!isProjectLoaded) {
    return (
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          padding: "16px 24px",
          width: "100%",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              color: "#353A4D",
              textAlign: "left",
              fontFamily: "Libre Franklin, Arial, sans-serif",
              lineHeight: "1.5em",
              letterSpacing: "-0.02em",
            }}
          >
            Enrollment Processing
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "120px",
          }}
        >
          <CircularProgress
            size={40}
            sx={{
              color: "#0076CE",
            }}
          />
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          padding: "16px 24px",
          width: "100%",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: "1",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                color: "#353A4D",
                textAlign: "left",
                fontFamily: "Libre Franklin, Arial, sans-serif",
                lineHeight: "1.5em",
                letterSpacing: "-0.02em",
              }}
            >
              Enrollment Processing
            </Typography>
          </Box>
          {projectDetails?.complete ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <IconButton
                onClick={() => handlePreviewDocument(projectDetails)}
                sx={{
                  border: "1px solid #0076CE",
                  borderRadius: "12px",
                  width: "40px",
                  height: "40px",
                  color: "#0076CE",
                  padding: "0px",
                  minWidth: "40px",
                  minHeight: "40px",
                  "&:hover": {
                    backgroundColor: "#E6F1FA",
                  },
                }}
              >
                <SvgEyeShow />
              </IconButton>
              <Button
                variant="outlined"
                disabled={isDownloading}
                sx={{
                  borderColor: "#0076CE",
                  color: "#0076CE",
                  fontWeight: 600,
                  fontSize: "12px",
                  textTransform: "none",
                  minWidth: "140px",
                  borderRadius: "12px",
                  padding: "8px 16px",
                  height: "40px",
                  minHeight: "40px",
                  borderWidth: "1px",
                  "&:hover": {
                    backgroundColor: "#E6F1FA",
                    borderColor: "#0076CE",
                  },
                  "&:disabled": {
                    borderColor: "#CCCCCC",
                    color: "#CCCCCC",
                  },
                }}
                onClick={handleDownloadClick}
                startIcon={
                  isDownloading ? <CircularProgress size={16} /> : undefined
                }
              >
                {isDownloading ? "Downloading..." : "Download Document"}
              </Button>
            </Box>
          ) : projectDetails?.document_url || lastActionDate ? (
            /* Show date and client info when document is sent but not signed */
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "8px",
                minHeight: "40px", // Match the height of the download buttons for consistent layout
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#666666",
                  fontFamily: "Inter, sans-serif",
                  lineHeight: "1.4em",
                }}
              >
                Sent to {projectDetails?.customerName || "Customer"}:
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1A1C1E",
                  fontFamily: "Inter, sans-serif",
                  lineHeight: "1.4em",
                }}
              >
                {projectDetails?.sent_to_customer || lastActionDate
                  ? formatShortMonthDate(
                      (projectDetails?.sent_to_customer ||
                        lastActionDate) as string
                    )
                  : ""}
              </Typography>
            </Box>
          ) : (
            /* Show Sign Document button when signature process hasn't started */
            <Button
              variant="outlined"
              sx={{
                backgroundColor: "#ffffff",
                borderColor: "#0076CE",
                color: "#0076CE",
                fontWeight: 600,
                fontSize: "12px",
                textTransform: "none",
                minWidth: "140px",
                borderRadius: "12px",
                padding: "8px 16px",
                height: "40px",
                minHeight: "40px",
                borderWidth: "1px",
                "&:hover": {
                  backgroundColor: "#E6F1FA",
                  borderColor: "#0076CE",
                },
              }}
              onClick={handleSignDocument}
            >
              Sign Document
            </Button>
          )}
        </Box>

        {shouldShowProgress && (
          <>
            <EnrollmentStatusStepper
              currentStep={currentStep}
              errorStep={errorStep}
              errorMessage={errorMessage}
              isComplete={projectDetails?.complete || false}
            />
            {errorStep && errorMessage && (
              <ErrorMessageBox
                errorStep={errorStep}
                errorMessage={errorMessage}
              />
            )}
          </>
        )}
      </Card>

      <ConfirmProjectSignatureModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleSubmitPDF}
        projectDetails={projectDetails}
        allowEmailEdit={false}
      />
    </>
  );
};

export default EnrollmentProcessingCard;
